import { FilterBar } from '@components/FilterBar';
import { GridItem } from '@components/GridItem';
import { HeavyPaper } from '@components/HeavyPaper';
import { LoadingBackdrop } from '@components/LoadingBackdrop';
import { VerticalGrid } from '@components/VerticalGrid';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useStepper } from '@hooks/useStepper';
import { useValueState } from '@hooks/useValueState';
import { Divider, makeStyles, Step, StepLabel, Stepper, Typography } from '@material-ui/core';
import { ICliente } from '@model/Cliente';
import { EnumClienteTipo, useClienteTipoCombo } from '@model/ClienteTipo';
import { EnumTipoCobrancaChip, IContratoCliente, useTipoCobrancaChipCombo } from '@model/ContratoCliente';
import { useContratoOperadoraCombo } from '@model/ContratoOperadora';
import ILinha from '@model/Linha';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';
import { useVendedorCombo } from '@model/Vendedor';
import { IValidarDocumentoResponse, IValidarNumeroResponse, useContratoClienteService } from '@services/ContratoClienteService';
import React from 'react';
import { HomeContratoCliente } from './Cliente';
import { HomeContratoContrato } from './Contrato';
import { HomeContratoLinha } from './Linha';
import { HomeContratoResumo } from './Resumo';

enum Steps {
    Linha = 0,
    Cliente = 1,
    Contrato = 2,
    Resumo = 3
}

export type HomeContratoProps = {
}

export function HomeContrato (props: HomeContratoProps) {
    const stepper = useStepper();
    const cls = useStyles();
    const loading = useLoading();
    const message = useMessage();

    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();
    const clienteTipoCombo = useClienteTipoCombo();
    const contratoOperadoraCombo = useContratoOperadoraCombo();
    const vendedorCombo = useVendedorCombo();
    const tipoCobrancaChipCombo = useTipoCobrancaChipCombo();

    /* LINHA */
    const linhaValueState = useValueState<IValidarNumeroResponse>();
    const linhaForm = useForm<Partial<ILinha>>({
        linhaId: useField(),
        contratoOperadoraId: useField(),
        contratoOperadoraEmpresaNome: useField(),
        contratoOperadoraOperadoraNome: useField(),
        numeroSemMascara: useField(),
        linhaEmpresa: useField("0"),
        vozIlimitada: useField("1"),
    });

    /* CLIENTE */
    const clienteValueState = useValueState<IValidarDocumentoResponse>();
    const clienteForm = useForm<Partial<ICliente>>({
        clienteId: useField(),

        clienteStatusId: useField(),
        clienteTipoId: useField(EnumClienteTipo.PessoaFisica),
        enderecoId: useField(),
    
        nome: useField(),
        cpf: useField(),
        rg: useField(),
        email: useField(),
        celular: useField(),
        localTrabalho: useField(),
        indicadoPor: useField(),
        telefoneIndicacao: useField(),
        diaVencimento: useField(10),
        remetenteAviso: useField(),
        whatsappEnvio: useField(),
        spc: useField(),
        observacao: useField(),
    
        dataNascimento: useField(),
        cartaoSus: useField(),
        orgao: useField(),
        cargo: useField(),

        telefoneAlternativo: useField(),
        telefoneAlternativo2: useField(),
        telefoneAlternativo3: useField(),
        telefoneAlternativo4: useField(),

        clienteStatusNome: useField(),
        clienteTipoNome: useField(),
    
        enderecoLogradouro: useField(),
        enderecoBairro: useField(),
        enderecoCidade: useField(),
        enderecoNumero: useField(),
        enderecoComplemento: useField(),
        enderecoCep: useField(),
        enderecoUf: useField(),
        enderecoLat: useField(),
        enderecoLon: useField(),
        enderecoReferencia: useField()
    });

    const contratoForm = useForm<Partial<IContratoCliente>>({
        inicio: useField(),
        valorPlano: useField(),
        valorAssessoria: useField(),
        franquiaGb: useField(),
        valorChip: useField(),
        tipoCobrancaChip: useField(EnumTipoCobrancaChip.None),
        vendedorId: useField(),
        vendedorNome: useField(),
        cobrarInteira: useField(),
        usuarioLinha: useField(),
    });

    const contratoClienteService = useContratoClienteService();

    const handleFinished = React.useCallback((contratoCliente: IContratoCliente) => {
        linhaValueState.set({
            linha: null,
            contratoCliente: null
        });

        clienteValueState.set({
            cliente: null,
            possuiDebitos: 0
        });

        linhaForm.reset();
        clienteForm.reset();
        contratoForm.reset();
        stepper.first();

        loading.start();
        contratoClienteService.gerarSegundaVia(contratoCliente.contratoClienteId)
            .catch(message.showBlobException)
            .finally(loading.stop);

    }, [clienteForm, clienteValueState, contratoClienteService, contratoForm, linhaForm, linhaValueState, loading, message.showBlobException, stepper]);

    const getContent = React.useCallback((step: number) => {
        switch (step) {
            case Steps.Linha: 
                return <HomeContratoLinha 
                    stepper={stepper}
                    linhaValueState={linhaValueState}
                    form={linhaForm}
                    loading={loading}
                    contratoOperadoraCombo={contratoOperadoraCombo}
                />

            case Steps.Cliente: 
                return <HomeContratoCliente 
                        stepper={stepper}
                        clienteValueState={clienteValueState}
                        form={clienteForm}
                        loading={loading}
                        clienteTipoCombo={clienteTipoCombo}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />

            case Steps.Contrato: 
                return <HomeContratoContrato 
                    form={contratoForm}
                    stepper={stepper}
                    vendedorCombo={vendedorCombo}
                    tipoCobrancaChipCombo={tipoCobrancaChipCombo}
                />

            case Steps.Resumo: 
                return <HomeContratoResumo 
                    stepper={stepper}
                    loading={loading}
                    linhaForm={linhaForm}
                    clienteForm={clienteForm}
                    contratoForm={contratoForm}
                    contratoOperadoraCombo={contratoOperadoraCombo}
                    clienteTipoCombo={clienteTipoCombo}
                    tipoCobrancaChipCombo={tipoCobrancaChipCombo}
                    onFinished={handleFinished}
                />
        }
    }, [clienteForm, clienteTipoCombo, clienteValueState, contratoForm, contratoOperadoraCombo, handleFinished, linhaForm, linhaValueState, loading, remetenteWhatsappCombo, stepper, tipoCobrancaChipCombo, vendedorCombo]);

    React.useEffect(() => {
        contratoForm.fields.vendedorId.setValue(linhaValueState.value?.linha?.vendedorId);
        contratoForm.fields.vendedorNome.setValue(linhaValueState.value?.linha?.vendedor?.nome);
    }, [contratoForm.fields.vendedorId, contratoForm.fields.vendedorNome, linhaValueState]);

    useEffectOnce(() => {
        contratoOperadoraCombo.loadOptions();
        clienteTipoCombo.loadOptions();
        remetenteWhatsappCombo.loadOptions();
        vendedorCombo.loadOptions();
        tipoCobrancaChipCombo.loadOptions();
    });

    return (
        <HeavyPaper>
            <VerticalGrid>
                <GridItem>
                    <FilterBar>
                        <Typography className={cls.title}>Cadastro de Contrato</Typography>                        
                    </FilterBar>
                </GridItem>
    
                <GridItem>
                    <VerticalGrid style={{position: 'relative'}}>
                        <GridItem>
                            <Stepper activeStep={stepper.step}>
                                <Step key={0}>
                                    <StepLabel>Telefone</StepLabel>
                                </Step>

                                <Step key={1}>
                                    <StepLabel>Cliente</StepLabel>
                                </Step>

                                <Step key={2}>
                                    <StepLabel>Contrato</StepLabel>
                                </Step>

                                <Step key={3}>
                                    <StepLabel>Confirmação</StepLabel>
                                </Step>
                            </Stepper>
                        </GridItem>

                        <Divider />

                        <GridItem>
                            {getContent(stepper.step)}
                        </GridItem>

                        <LoadingBackdrop isLoading={loading.isLoading} />
                    </VerticalGrid>
                </GridItem>
            </VerticalGrid>
        </HeavyPaper>
    )
}

const useStyles = makeStyles(theme => ({
    title: {
        fontWeight: 'bold',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1)
    }
}));